<div class="content-creation" *ngIf="tabData && (tabData.universities || tabData.platform_analysis)">
  <!-- Header -->
  <div class="header">
    <h2>Content Creation Analysis - {{ config?.entityLabel || 'University' }} Strategy</h2>
    <div class="overall-score" *ngIf="getOverallContentScore()">
      <span class="score-label">Overall Content Score:</span>
      <span class="score-value" [ngClass]="getScoreClass(getOverallContentScore())"
        [style.color]="getScoreColor(getOverallContentScore())">
        {{ getOverallContentScore() }}/100
      </span>
    </div>
  </div>

  <!-- Platform-Based Content Analysis -->
  <div class="platform-analysis" *ngIf="tabData.platform_analysis">
    <div class="platform-category" *ngFor="let category of getPlatformCategories()">
      <h3 class="platform-category-title">{{ category }}</h3>
      <div class="platform-grid">
        <div class="platform-item" *ngFor="let platform of getPlatformsByCategory(category)">
          <mat-card class="platform-card">
            <mat-card-header>
              <mat-card-title>{{ platform.sub_platform }}</mat-card-title>
              <mat-card-subtitle>
                <span class="status-badge"
                  [class]="'status-' + platform.current_status?.toLowerCase().replace(' ', '-')">
                  {{ platform.current_status }}
                </span>
                <span class="priority-badge" [class]="'priority-' + platform.priority?.toLowerCase()">
                  {{ platform.priority }}
                </span>
              </mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <!-- Specific Issue -->
              <div class="specific-issue">
                <h4>Issue:</h4>
                <p>{{ platform.specific_issue }}</p>
              </div>

              <!-- Improvement Needed -->
              <div class="improvement-needed">
                <h4>Improvement Needed:</h4>
                <p>{{ platform.improvement_needed }}</p>
              </div>

              <!-- Estimated Impact -->
              <div class="estimated-impact">
                <h4>Expected Impact:</h4>
                <p class="impact-text">{{ platform.estimated_impact }}</p>
              </div>

              <!-- Generate Button -->
              <div class="generate-section" *ngIf="platform.ready_to_generate">
                <button mat-raised-button color="primary" class="generate-button" (click)="generateContent(platform)">
                  <mat-icon>create</mat-icon>
                  Generate Sample Template
                </button>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>
    </div>
  </div>

  <!-- Content Recommendations Summary -->
  <div class="recommendations-summary"
    *ngIf="tabData.content_recommendations && tabData.content_recommendations.length > 0">
    <h3>Content Creation Recommendations</h3>
    <div class="recommendations-grid">
      <div class="recommendation-card" *ngFor="let rec of tabData.content_recommendations">
        <mat-card>
          <mat-card-header>
            <mat-card-title>{{ rec.title }}</mat-card-title>
            <mat-card-subtitle>
              <span class="content-type-badge">{{ rec.content_type }}</span>
              <span class="piece-type-badge">{{ rec.piece_type }}</span>
            </mat-card-subtitle>
          </mat-card-header>
          <mat-card-content>
            <div class="recommendation-keywords" *ngIf="rec.keywords">
              <h4>Keywords:</h4>
              <mat-chip-set>
                <mat-chip *ngFor="let keyword of rec.keywords">{{ keyword }}</mat-chip>
              </mat-chip-set>
            </div>
            <div class="recommendation-meta">
              <p><strong>Impact Score:</strong> {{ rec.impact_score }}/100</p>
              <p><strong>Estimated Effort:</strong> {{ rec.estimated_effort }}</p>
            </div>
            <div class="recommendation-actions">
              <button mat-raised-button color="primary" class="generate-button"
                (click)="generateContentFromRecommendation(rec)">
                <mat-icon>create</mat-icon>
                Generate Content
              </button>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    </div>
  </div>

  <!-- Legacy Universities Content Analysis (Fallback) -->
  <div class="universities-section" *ngIf="!tabData.platform_analysis && tabData.universities">
    <div class="university-card" *ngFor="let university of tabData.universities">
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ university.name }}</mat-card-title>
          <mat-card-subtitle>Content Score: {{ university.content_status?.content_score || 0
            }}/100</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <!-- Content Types Breakdown -->
          <div class="content-types" *ngIf="university.content_status?.content_types">
            <h4>Content Types</h4>
            <div class="content-type-grid">
              <div class="content-type-item" *ngFor="let type of university.content_status.content_types">
                <div class="type-header">
                  <span class="type-name">{{ type.type }}</span>
                  <span class="type-count">{{ type.count }} pieces</span>
                </div>
                <mat-progress-bar [value]="type.quality_score" [color]="getQualityColor(type.quality_score)">
                </mat-progress-bar>
                <div class="type-quality">Quality: {{ type.quality_score }}%</div>
              </div>
            </div>
          </div>

          <!-- Competitive Gaps -->
          <div class="competitive-gaps" *ngIf="university.competitive_gaps && university.competitive_gaps.length > 0">
            <h4>Competitive Gaps</h4>
            <div class="gaps-list">
              <div class="gap-item" *ngFor="let gap of university.competitive_gaps">
                <div class="gap-header">
                  <span class="gap-type">{{ gap.content_type }}</span>
                  <span class="gap-priority" [class]="'priority-' + gap.priority?.toLowerCase()">
                    {{ gap.priority }}
                  </span>
                </div>
                <div class="gap-details">
                  <div class="gap-current">Current: {{ gap.current_status }}</div>
                  <div class="gap-percentage">Gap: {{ gap.gap_percentage }}%</div>
                </div>
                <div class="gap-recommendations" *ngIf="gap.recommendations">
                  <h5>Recommendations:</h5>
                  <ul>
                    <li *ngFor="let rec of gap.recommendations">{{ rec }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Content Recommendations -->
          <div class="content-recommendations"
            *ngIf="university.recommendations && university.recommendations.length > 0">
            <h4>Content Recommendations</h4>
            <div class="recommendations-list">
              <div class="recommendation-item" *ngFor="let rec of university.recommendations">
                <div class="recommendation-header">
                  <span class="content-type-badge">{{ rec.content_type }}</span>
                  <span class="piece-type-badge">{{ rec.piece_type }}</span>
                  <span class="impact-score">Impact: {{ rec.impact_score }}/100</span>
                </div>
                <h5 class="recommendation-title">{{ rec.title }}</h5>
                <div class="recommendation-keywords" *ngIf="rec.keywords">
                  <mat-chip-set>
                    <mat-chip *ngFor="let keyword of rec.keywords">{{ keyword }}</mat-chip>
                  </mat-chip-set>
                </div>
                <div class="recommendation-footer">
                  <span class="estimated-effort">Effort: {{ rec.estimated_effort }}</span>
                </div>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>
</div>

<!-- No Data Message -->
<div class="no-data" *ngIf="!tabData || (!tabData.universities && !tabData.platform_analysis)">
  <p>No content creation data available.</p>
  <p>Run a content analysis to generate Content Creation data.</p>
</div>

<!-- Content Generation Popup -->
<app-content-generation-popup [recommendation]="getRecommendationForPopup()" [isVisible]="showContentGenerationPopup"
  (close)="closeContentGenerationPopup()">
</app-content-generation-popup>
